import { Observable } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { DateDifferenceService } from 'src/app/shared/services/date-difference.service';

import { ContextMessageInfo, LazyloadPublicContextMessageResponse, PublicContextMessage } from '../../shared/model';
import { TaskProcessContext } from '../../shared/task-process-context.class';
import { MESSAGE_PAGE_SIZE, TaskProcessService } from '../../shared/task-process.service';

export class TaskPublicContext {
  rootMessage: PublicContextMessage;
  loadedChunksBeforeProcesedMessage: PublicContextMessage[][] = [];
  processedMessage: PublicContextMessage;
  loadedChunksAfterProcesedMessage: PublicContextMessage[][] = [];
  hasContextMessages: boolean; // true if there is at least one message (other than the processed message)
  twitterPublicContextTasks = null;
  isTwitterPublicTask: boolean;

  private processedMessageId: number;
  private notYetLoadedIdsBeforeProcessedMessage: number[] = [];
  private notYetLoadedIdsAfterProcessedMessage: number[] = [];

  private contextMessageInfosById = new Map<number, ContextMessageInfo>();
  private rootMessageInfosById = new Map<number, ContextMessageInfo>();
  constructor(
    private taskId: number,
    private dds: DateDifferenceService,
    private taskProcessService: TaskProcessService,
    private taskProcessContext: TaskProcessContext
  ) {
    this.isTwitterPublicTask =
      taskProcessContext.site.type === SiteTypeEnum.Twitter && !taskProcessContext.processedTaskInfo.isPrivate;
    if (this.isTwitterPublicTask) {
      taskProcessService.getTwitterPublicContext(taskId).subscribe({
        next: response => {
          const formatter = message => {
            const { time, isFromBrand, text, url, id, fromUserId, fromUserName, groups } = message;
            const item = {
              type: isFromBrand ? 'message' : 'task',
              datetime: time,
              text,
              externalUrl: url,
              isFromBrand,
              from: {
                id,
                name: fromUserName,
                external_id: fromUserId,
                handle: `@${fromUserName}`,
              },
              content: {
                text,
                attachments: [],
              },
              timeDelta: this.dds.compactDateDifferenceToNow(time),
            } as any;

            if (groups?.length) {
              item.groups = groups.map(group => group.map(formatter));
            }

            return item;
          };
          this.twitterPublicContextTasks = (response || []).map(message => {
            return formatter(message);
          });
        },
      });
    }
  }

  loadContextAndInitialMessages(): Observable<LazyloadPublicContextMessageResponse> {
    return this.taskProcessService.getTaskPublicContext(this.taskId).pipe(
      tap(context => {
        context.contextMessageInfos.forEach(info => this.contextMessageInfosById.set(info.id, info));
        if (this.taskProcessContext.publicContextHasTreeStructure()) {
          this.buildTreeStructure();
        } else {
          // for twitter public, there is no tree structure, which we simulate by considering that all messages are a one-element tree
          this.rootMessageInfosById = this.contextMessageInfosById;
          this.assignLevelZeroToAllMessages();
        }
        this.fillMessagesBeforeAndAfter();
        this.hasContextMessages = this.contextMessageInfosById.size > 1;
      }),
      mergeMap(() => this.loadInitialMessages())
    );
  }

  // loads the root message, the processed messge,
  // up to 10 messages before the processed message,
  // and up to 10 messages after the processed message
  private loadInitialMessages(): Observable<LazyloadPublicContextMessageResponse> {
    const HALF_PAGE_SIZE = MESSAGE_PAGE_SIZE / 2;
    // Note that splice() conveniently splices the whole array if its length is less than 10
    const beforeProcessedIds = this.notYetLoadedIdsBeforeProcessedMessage.splice(-HALF_PAGE_SIZE, HALF_PAGE_SIZE);
    const afterProcessedIds = this.notYetLoadedIdsAfterProcessedMessage.splice(0, HALF_PAGE_SIZE);
    const contextMessageIds = [
      this.rootMessageId(),
      this.processedMessageId,
      ...beforeProcessedIds,
      ...afterProcessedIds,
    ];
    return this.taskProcessService
      .lazyloadPublicContextMessages(contextMessageIds, this.taskProcessContext.site.id)
      .pipe(
        tap(response => {
          const messagesById = response.contextMessagesById;
          this.updateLevelForLoadedMessages(messagesById);
          if (this.rootMessageId() !== this.processedMessageId) {
            this.rootMessage = messagesById[this.rootMessageId()];
          }
          this.processedMessage = messagesById[this.processedMessageId];
          const beforeMessages = beforeProcessedIds.map(id => messagesById[id]);
          this.loadedChunksBeforeProcesedMessage.unshift(beforeMessages);
          const afterMessages = afterProcessedIds.map(id => messagesById[id]);
          this.loadedChunksAfterProcesedMessage.push(afterMessages);
          this.updateMessagesTimeDelta();
        })
      );
  }

  canLoadMessagesBefore() {
    return this.notYetLoadedIdsBeforeProcessedMessage.length > 0;
  }

  canLoadMessagesAfter() {
    return this.notYetLoadedIdsAfterProcessedMessage.length > 0;
  }

  sizeOfNextChunkOfMessagesBefore() {
    return Math.min(this.notYetLoadedIdsBeforeProcessedMessage.length, MESSAGE_PAGE_SIZE);
  }

  loadMessagesBefore(): Observable<LazyloadPublicContextMessageResponse> {
    const beforeProcessedIds = this.notYetLoadedIdsBeforeProcessedMessage.splice(-MESSAGE_PAGE_SIZE, MESSAGE_PAGE_SIZE);
    return this.taskProcessService
      .lazyloadPublicContextMessages(beforeProcessedIds, this.taskProcessContext.site.id)
      .pipe(
        tap(response => {
          const messagesById = response.contextMessagesById;
          this.updateLevelForLoadedMessages(messagesById);
          const beforeMessages = beforeProcessedIds.map(id => messagesById[id]);
          this.loadedChunksBeforeProcesedMessage.unshift(beforeMessages);
          this.updateMessagesTimeDelta();
        })
      );
  }

  loadMessagesAfter(): Observable<LazyloadPublicContextMessageResponse> {
    const afterProcessedIds = this.notYetLoadedIdsAfterProcessedMessage.splice(0, MESSAGE_PAGE_SIZE);
    return this.taskProcessService
      .lazyloadPublicContextMessages(afterProcessedIds, this.taskProcessContext.site.id)
      .pipe(
        tap(response => {
          const messagesById = response.contextMessagesById;
          this.updateLevelForLoadedMessages(messagesById);
          const afterMessages = afterProcessedIds.map(id => messagesById[id]);
          this.loadedChunksAfterProcesedMessage.push(afterMessages);
          this.updateMessagesTimeDelta();
        })
      );
  }

  updateMessagesTimeDelta() {
    for (const message of this.allMessages()) {
      if (message) {
        message.timeDelta = this.dds.compactDateDifferenceToNow(message.datetime);
      }
    }
  }

  private updateLevelForLoadedMessages(messagesById: { [id: string]: PublicContextMessage }) {
    for (const id of Object.keys(messagesById)) {
      messagesById[id].level = this.contextMessageInfosById.get(Number(id)).level;
    }
  }

  /**
   * Sets the depth level (0 is a root) for the message infos,
   * as well as an array of children, for each message info.
   *
   * level 0: messages that don't have a parent_id
   * level n + 1: messages whose parent id is the id of a level n message
   * We stop when no messages have been found for a given level
   * Any remaining messages (normally none if they do form a tree) are set to level 0
   */
  private buildTreeStructure() {
    for (const messageInfo of this.contextMessageInfosById.values()) {
      if (!messageInfo.parentId) {
        this.rootMessageInfosById.set(messageInfo.id, messageInfo);
        this.setLevelAndEmptyChildren(messageInfo, 0);
      }
    }
    let previousLevelMessagesById = this.rootMessageInfosById;
    let currentLevel = 1;
    while (previousLevelMessagesById.size > 0) {
      previousLevelMessagesById = this.findUpdateAndReturnMessagesOfLevel(previousLevelMessagesById, currentLevel++);
    }
    for (const messageInfo of this.contextMessageInfosById.values()) {
      if (messageInfo.level === undefined) {
        this.setLevelAndEmptyChildren(messageInfo, 0);
      }
    }
  }

  private findUpdateAndReturnMessagesOfLevel(previousLevelMessagesById, currentLevel) {
    const currentLevelMessagesById = new Map<number, ContextMessageInfo>();
    for (const messageInfo of this.contextMessageInfosById.values()) {
      if (messageInfo.level === undefined && previousLevelMessagesById.has(messageInfo.parentId)) {
        this.setLevelAndEmptyChildren(messageInfo, currentLevel);
        previousLevelMessagesById.get(messageInfo.parentId).children.push(messageInfo);
        currentLevelMessagesById.set(messageInfo.id, messageInfo);
      }
    }
    return currentLevelMessagesById;
  }

  private assignLevelZeroToAllMessages() {
    for (const messageInfo of this.contextMessageInfosById.values()) {
      this.setLevelAndEmptyChildren(messageInfo, 0);
    }
  }

  private setLevelAndEmptyChildren(messageInfo, level) {
    messageInfo.level = level;
    messageInfo.children = [];
  }

  private fillMessagesBeforeAndAfter() {
    let foundProcessedMessage = false;
    const rootMessageId = this.rootMessageId();
    this.walkTrees(messageInfo => {
      if (messageInfo.isProcessedTask) {
        foundProcessedMessage = true;
        this.processedMessageId = messageInfo.id;
      }
      if (messageInfo.id === rootMessageId || messageInfo.isProcessedTask) {
        // skip it
      } else if (!foundProcessedMessage) {
        this.notYetLoadedIdsBeforeProcessedMessage.push(messageInfo.id);
      } else {
        this.notYetLoadedIdsAfterProcessedMessage.push(messageInfo.id);
      }
    });
  }

  private walkTrees(callback?: (m: ContextMessageInfo) => void) {
    for (const root of this.rootMessageInfosById.values()) {
      this.walkMessageAndChildren(root, callback);
    }
  }

  private walkMessageAndChildren(message, callback) {
    if (callback) {
      callback(message);
    }
    message.children.forEach(child => this.walkMessageAndChildren(child, callback));
  }

  // returns the id of the root message if the messages form a well-formed tree,
  // and in any case the id of the oldest message with level 0 as set by buildTreeStructure()
  private rootMessageId() {
    return this.rootMessageInfosById.values().next().value.id;
  }

  private allMessages() {
    const result = this.rootMessage ? [this.rootMessage] : [];
    result.push(this.processedMessage);
    for (const chunk of this.loadedChunksBeforeProcesedMessage) {
      result.push(...chunk);
    }
    for (const chunk of this.loadedChunksAfterProcesedMessage) {
      result.push(...chunk);
    }
    return result;
  }
}
